/**
 * Purpose: decide which scale one plan sheet is drawn at, when the scale
 *          readings disagree (scale bar, printed ratio on its paper, the
 *          scales of the other sheets of the same building).
 *
 * Input:   one image, the plan crop the footprint step traced, with the
 *          traced outlines drawn on it; per candidate, the size those
 *          outlines would have in reality.
 * Output:  `project_file_drawing_scale` — the plausible candidate, or none,
 *          with the evidence on the drawing it rests on.
 * Consumer: `server/src/services/project-file-batches/project-file-drawing-scale.ts`.
 *
 * The model judges, the code measures: it never sees pixels per metre, only
 * what each candidate would make the building (Henrik 2026-10-15).
 */

import { z } from 'zod';
import type { OutlineInMetres } from './ffu-drawing-scale.helpers';

export const PROJECT_FILE_DRAWING_SCALE_SCHEMA_NAME =
  'project_file_drawing_scale' as const;

export const drawingScaleAiSchema = z.object({
  evidence: z
    .string()
    .describe(
      'What on the drawing decides it, with the numbers: printed room or apartment areas, a dimension chain, door widths, stairs, parking bays, furniture. One or two sentences.',
    ),
  choice: z
    .string()
    .nullable()
    .describe(
      'The letter of the plausible candidate. Null when none is plausible or the drawing gives nothing to judge by.',
    ),
  confidence: z
    .enum(['low', 'medium', 'high'])
    .describe('How sure the evidence makes you.'),
});
export type DrawingScaleAi = z.infer<typeof drawingScaleAiSchema>;

export const buildDrawingScaleJsonSchema = (): Record<string, unknown> => {
  const { $schema: _ignored, ...rest } = z.toJSONSchema(
    drawingScaleAiSchema,
  ) as Record<string, unknown> & { $schema?: string };
  return rest;
};

const RULES = [
  'The image is one plan drawing. The building outline traced on it is drawn in red, numbered when there are several.',
  'The scale of this sheet was read in more than one way, and the readings disagree. Each candidate below says how large the outlines would be in reality at that scale.',
  '',
  'Decide which candidate is plausible from what the drawing itself shows, for example:',
  '- areas printed in rooms or apartments, which fit inside the outline with room to spare for walls, stairs and shafts;',
  '- printed dimensions along a wall or a dimension chain;',
  '- doors about 0.9 to 1 m wide, stair flights about 1.2 m wide, parking bays about 2.5 by 5 m, beds about 2 m long, kitchens and bathrooms of ordinary size.',
  'Do not decide by the printed ratio or the scale bar: they are what disagree. Give the letter, or null when none fits or nothing on the drawing can tell.',
].join('\n');

const metres = (value: number) => value.toFixed(1);

/** `A`, `B`, … for candidate `index`. */
export const scaleCandidateLetter = (index: number): string =>
  String.fromCharCode(65 + index);

/** The user prompt for one plan crop and its candidates. */
export const buildDrawingScalePrompt = (
  candidates: readonly (readonly OutlineInMetres[])[],
): string => {
  const lines = candidates.map((outlines, index) => {
    const parts = outlines.map((outline, n) => {
      const printed =
        outline.printedM2 > 0
          ? `, areas printed inside sum to ${Math.round(outline.printedM2)} m²`
          : '';
      const name = outlines.length > 1 ? `outline ${n + 1}` : 'the outline';
      return `${name} ${metres(outline.widthM)} × ${metres(outline.heightM)} m, ${Math.round(outline.areaM2)} m²${printed}`;
    });
    return `${scaleCandidateLetter(index)}: ${parts.join('; ')}`;
  });
  return `${RULES}\n\nCandidates:\n${lines.join('\n')}`;
};
